import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import {
  getQuestionSchemaRogerdocQuestionSchemaGet,
  getVisitSchemaRogerdocV2VisitsVisitIdSchemaGet,
  listEnabledSectionsBespokeTemplatesEnabledGet,
  validateOasisVisitsVisitIdValidateOasisPost,
} from '../../services/api/sdk.gen';
import { client } from '../../services/api';
import { VisitTypeId } from '../../services/api/models/VisitTypeId';
import {
  OASISSchema,
  OASISFormData,
  Question,
  FormSchemaVersion,
} from './types';
import type { OasisValidationError } from '../../services/api/types.gen';
import { OASISQuestionRenderer } from './OASISQuestionRenderer';
import { OASISNavigation } from './OASISNavigation';
import { VisitActiveDiagnosis } from './VisitActiveDiagnosis';
import {
  resolveBespokeSection,
  type SchemaQuestionOption,
  type BespokeDbConfig,
} from './bespoke/bespokeRegistry';
import type { EnabledBespokeEntry } from '../../services/api/types.gen';
import { useOASISFormStore } from '../../store/slices/oasisFormSlice';
import { useDebounce } from '../../hooks/useDebounce';
import {
  getOASISFormData,
  getSubitemClearUpdates,
  initializePseudoQuestionDefaults,
} from './utils';
import {
  collectSubitemOrderByParentQid,
  resolveQuestionSpan,
  reorderNestedSubitemsInQuestions,
  GridSpan,
  SPAN_CLASSES,
  getSubgroupChildSpanClass,
} from './OASISFormUtils';
import { Button, Card, CardContent } from '@roger/ui-components';
import {
  Loader2,
  Check,
  GripVertical,
  Copy,
  ArrowUpDown,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import { FeatureFlag } from '../../constants/featureFlags';

interface OASISFormProps {
  visitId: string;
  visitType: VisitTypeId;
  initialData?: OASISFormData;
  onSave: (data: OASISFormData) => void | Promise<void>;
  hasActiveSignatures?: boolean;
  showInlineValidation?: boolean;
}

const DEFAULT_GRID_SPAN: GridSpan = 3;
const QUESTION_SPAN_OPTIONS: GridSpan[] = [1, 2, 3, 4, 6, 8, 9, 12];
type SectionGridSpan = 1 | 2 | 3 | 4;
const DEFAULT_SECTION_SPAN: SectionGridSpan = 2;
const SECTION_SPAN_OPTIONS: SectionGridSpan[] = [1, 2, 3, 4];
const SECTION_SPAN_CLASSES: Record<SectionGridSpan, string> = {
  1: 'col-span-1',
  2: 'col-span-2',
  3: 'col-span-3',
  4: 'col-span-4',
};

/** A flattened grid item - either a standalone question or context about its parent */
interface GridItem {
  question: Question;
  /** If this item came from a pseudo parent, track the parent for auto-setting "Yes" */
  pseudoParent?: Question;
  span: GridSpan;
  /**
   * When the question has subitems, this holds them as individually span-configurable cells
   * rendered as a mini-grid inside the parent card in Arrange mode.
   */
  subgroupChildren?: GridItem[];
}

/** Check if a visit type is an OASIS form (has sidebar navigation) */
const isOASISVisitType = (visitType: VisitTypeId): boolean =>
  visitType.startsWith('OASIS_');

/** Sentinel category key for the hardcoded Active Diagnosis section */
export const ACTIVE_DIAGNOSIS_CATEGORY = '__ACTIVE_DIAGNOSIS__';

/**
 * Recursively collect all subitems of a question as individual GridItems for
 * the sub-group mini-grid shown in Arrange mode.
 */
const buildSubgroupItems = (
  question: Question,
  spanOverrides?: Record<string, GridSpan>
): GridItem[] => {
  const children: GridItem[] = [];
  for (const branchItems of Object.values(question.subitems ?? {})) {
    for (const sub of branchItems) {
      const subHasSubitems =
        sub.subitems && Object.keys(sub.subitems).length > 0;
      children.push({
        question: sub,
        pseudoParent: question,
        span: subHasSubitems
          ? resolveSubgroupContainerSpan(sub, spanOverrides)
          : resolveQuestionSpan(sub, spanOverrides),
        ...(subHasSubitems
          ? { subgroupChildren: buildSubgroupItems(sub, spanOverrides) }
          : {}),
      });
    }
  }
  return children;
};

const getDirectSubitemCount = (question: Question): number =>
  Object.values(question.subitems ?? {}).reduce(
    (sum, subitems) => sum + subitems.length,
    0
  );

/**
 * Resolve container span for subgroup parents:
 * - explicit override / schema width wins
 * - otherwise use a compact default based on how many direct subitems exist
 */
const resolveSubgroupContainerSpan = (
  question: Question,
  spanOverrides?: Record<string, GridSpan>
): GridSpan => {
  if (spanOverrides?.[question.qid] !== undefined) {
    return spanOverrides[question.qid];
  }
  if (
    question.roger_emr_display_width !== undefined &&
    question.roger_emr_display_width >= 1 &&
    question.roger_emr_display_width <= 12
  ) {
    return question.roger_emr_display_width as GridSpan;
  }

  const directSubitems = getDirectSubitemCount(question);
  if (directSubitems <= 1) return 3;
  if (directSubitems <= 2) return 6;
  if (directSubitems <= 4) return 9;
  return 12;
};

/**
 * Flattens the question list so pseudo questions' subitems
 * become individual grid items instead of being nested.
 * Questions that have their own subitems carry a `subgroupChildren` list
 * so Arrange mode can render them as an expandable mini-grid.
 */
const flattenQuestions = (
  questions: Question[],
  spanOverrides?: Record<string, GridSpan>
): GridItem[] => {
  const items: GridItem[] = [];

  for (const question of questions) {
    const isPseudo = question.is_pseudo === true;

    if (isPseudo && question.subitems?.['Yes']) {
      for (const subitem of question.subitems['Yes']) {
        const subHasSubitems =
          subitem.subitems && Object.keys(subitem.subitems).length > 0;
        items.push({
          question: subitem,
          pseudoParent: question,
          span: subHasSubitems
            ? resolveSubgroupContainerSpan(subitem, spanOverrides)
            : resolveQuestionSpan(subitem, spanOverrides),
          ...(subHasSubitems
            ? { subgroupChildren: buildSubgroupItems(subitem, spanOverrides) }
            : {}),
        });
      }
    } else if (question.subitems && Object.keys(question.subitems).length > 0) {
      items.push({
        question,
        span: resolveSubgroupContainerSpan(question, spanOverrides),
        subgroupChildren: buildSubgroupItems(question, spanOverrides),
      });
    } else {
      items.push({
        question,
        span: resolveQuestionSpan(question, spanOverrides),
      });
    }
  }

  return items;
};

/** Format a category key into a readable label */
const formatCategoryLabel = (category: string): string =>
  category
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .trim();

/**
 * Reconstruct the top-level question array from a reordered flat GridItem list.
 * Groups consecutive items sharing the same pseudoParent back into one pseudo question.
 */
const reconstructQuestions = (
  items: GridItem[],
  originalQuestions: Question[]
): Question[] => {
  const result: Question[] = [];
  const seenPseudoParents = new Set<string>();
  const pseudoChildrenByParentQid: Record<string, Question[]> = {};

  for (const item of items) {
    if (!item.pseudoParent) continue;
    const parentQid = item.pseudoParent.qid;
    if (!pseudoChildrenByParentQid[parentQid]) {
      pseudoChildrenByParentQid[parentQid] = [];
    }
    pseudoChildrenByParentQid[parentQid].push(item.question);
  }

  for (const item of items) {
    if (item.pseudoParent) {
      const parentQid = item.pseudoParent.qid;
      if (!seenPseudoParents.has(parentQid)) {
        seenPseudoParents.add(parentQid);
        const originalParent = originalQuestions.find(q => q.qid === parentQid);
        if (originalParent) {
          const reorderedChildren = pseudoChildrenByParentQid[parentQid];
          if (originalParent.subitems?.['Yes'] && reorderedChildren) {
            result.push({
              ...originalParent,
              subitems: {
                ...originalParent.subitems,
                Yes: reorderedChildren,
              },
            });
          } else {
            result.push(originalParent);
          }
        }
      }
    } else {
      result.push(item.question);
    }
  }

  return result;
};

const formatValidationDescription = (description: string): string => {
  return description
    .replace(/\[blank\]|\[\^\]/gi, 'blank')
    .replace(/\[Checked \(Yes\)\]/gi, 'Yes')
    .replace(/\bmust not equal\s+blank\b/gi, 'cannot be blank')
    .replace(/\bmust equal\s+Yes\b/gi, 'must be Yes')
    .replace(/\bcontrols\b/gi, 'requires')
    .replace(/\s+/g, ' ')
    .trim();
};

const VALIDATION_HIGHLIGHT_PATTERN =
  /"[^"]+"|\b[A-Z]\d{4}_[A-Z0-9_]+\b|\b(must|cannot|requires|blank|Yes|No)\b/gi;

const splitValidationRule = (text: string) => {
  const ifThenMatch = text.match(/^If\s+(.+?)(?:,\s*then|\s+then)\s+(.+)$/i);
  if (ifThenMatch) {
    return {
      type: 'ifThen' as const,
      condition: ifThenMatch[1].trim(),
      expectation: ifThenMatch[2].trim(),
    };
  }

  const requiresMatch = text.match(/^(.+?)\s+requires\s+(.+)$/i);
  if (requiresMatch) {
    return {
      type: 'paired' as const,
      condition: requiresMatch[1].trim(),
      expectation: requiresMatch[2].trim(),
    };
  }

  const colonIdx = text.indexOf(':');
  if (colonIdx > 0) {
    return {
      type: 'paired' as const,
      condition: text.slice(0, colonIdx).trim(),
      expectation: text.slice(colonIdx + 1).trim(),
    };
  }

  return { type: 'single' as const, condition: '', expectation: text };
};

const parseValidationDetail = (detail?: string) => {
  if (!detail) return null;
  const trimmed = detail.trim();
  if (!trimmed) return null;

  const notInMatch = trimmed.match(/^'?(.*?)'?\s+not in:\s+(.+)$/i);
  if (notInMatch) {
    const entered = notInMatch[1]?.trim();
    const allowedPart = notInMatch[2] || '';
    const allowedCodes = Array.from(
      new Set(
        [...allowedPart.matchAll(/\b([A-Z0-9]{1,4})\s*\(([^)]+)\)/g)].map(
          m => `${m[1]} - ${m[2]}`
        )
      )
    );
    return {
      type: 'notIn' as const,
      entered,
      allowedCodes,
      raw: trimmed,
    };
  }

  const assignmentMatch = trimmed.match(/^"?(.*?)"?\s*=\s*(.+)$/);
  if (assignmentMatch) {
    return {
      type: 'assignment' as const,
      label: assignmentMatch[1]?.trim(),
      value: assignmentMatch[2]?.trim(),
      raw: trimmed,
    };
  }

  return { type: 'text' as const, raw: trimmed };
};

const buildQidToCategoryMap = (schema: OASISSchema | null) => {
  const map: Record<string, string> = {};
  if (!schema) return map;

  const collectQuestion = (question: Question, category: string) => {
    if (question.qid) {
      map[question.qid] = category;
    }
    if (!question.subitems) return;
    Object.values(question.subitems).forEach(subitems => {
      subitems.forEach(subitem => collectQuestion(subitem, category));
    });
  };

  Object.entries(schema).forEach(([category, questions]) => {
    questions.forEach(question => collectQuestion(question, category));
  });

  return map;
};

const buildQidToQuestionText = (schema: OASISSchema | null) => {
  const map: Record<string, string> = {};
  if (!schema) return map;

  const collect = (question: Question) => {
    if (question.qid && question.question) {
      map[question.qid] = question.question;
    }
    if (!question.subitems) return;
    Object.values(question.subitems).forEach(subitems => {
      subitems.forEach(sub => collect(sub));
    });
  };

  Object.values(schema).forEach(questions => {
    questions.forEach(q => collect(q));
  });

  return map;
};

export const OASISForm: React.FC<OASISFormProps> = ({
  visitId,
  visitType,
  initialData,
  onSave,
  hasActiveSignatures = false,
  showInlineValidation = false,
}) => {
  const {
    schema,
    formData,
    currentCategory,
    isLoading,
    error,
    schemaVersion,
    setSchema,
    setFormData,
    setCurrentCategory,
    setIsLoading,
    setError,
    setLastSaved,
    setSchemaVersion,
  } = useOASISFormStore();

  const { hasAnyPermission, hasFeatureFlag } = useStore(
    useShallow(state => ({
      hasAnyPermission: state.hasAnyPermission,
      hasFeatureFlag: state.hasFeatureFlag,
    }))
  );
  const canManageEmr = hasAnyPermission(['emr:manage']);
  const useVersionedFormSchemas = hasFeatureFlag(
    FeatureFlag.VERSIONED_FORM_SCHEMAS
  );

  const [categories, setCategories] = useState<string[]>([]);
  const [saveStatus, setSaveStatus] = useState<
    'idle' | 'saving' | 'saved' | 'error'
  >('idle');
  const [arrangeMode, setArrangeMode] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [subgroupDragIndex, setSubgroupDragIndex] = useState<{
    parentQid: string;
    index: number;
  } | null>(null);
  const [subgroupDragOverIndex, setSubgroupDragOverIndex] = useState<{
    parentQid: string;
    index: number;
  } | null>(null);
  const [sectionDragIndex, setSectionDragIndex] = useState<number | null>(null);
  const [sectionDragOverIndex, setSectionDragOverIndex] = useState<
    number | null
  >(null);
  const [copied, setCopied] = useState(false);
  const [spanOverrides, setSpanOverrides] = useState<Record<string, GridSpan>>(
    {}
  );
  const [sectionSpanOverrides, setSectionSpanOverrides] = useState<
    Record<string, SectionGridSpan>
  >({});
  const [touchedQids, setTouchedQids] = useState<Set<string>>(new Set());
  /** Per-form DB bespoke template map (schema version → section → entry). */
  const [bespokeDbConfig, setBespokeDbConfig] = useState<BespokeDbConfig>(
    () => new Map()
  );
  const [inlineValidationErrors, setInlineValidationErrors] = useState<
    OasisValidationError[]
  >([]);
  const [inlineValidationLoading, setInlineValidationLoading] = useState(false);
  const [inlineValidationErrorMessage, setInlineValidationErrorMessage] =
    useState<string | null>(null);
  const [hasValidatedOnce, setHasValidatedOnce] = useState(false);
  const [isScrubberExpanded, setIsScrubberExpanded] = useState(false);
  const [scrubberTab, setScrubberTab] = useState<'section' | 'misc' | 'all'>(
    'section'
  );
  const [highlightedQid, setHighlightedQid] = useState<string | null>(null);
  const hasRunInitialValidation = useRef(false);

  /** Avoid resetting local edits when parent `initialData` updates after autosave. */
  const hydratedVisitIdRef = useRef<string | null>(null);

  const isOASIS = isOASISVisitType(visitType);
  const qidToCategoryMap = useMemo(
    () => buildQidToCategoryMap(schema),
    [schema]
  );
  const qidToQuestionText = useMemo(
    () => buildQidToQuestionText(schema),
    [schema]
  );
  const touchedCategories = useMemo(() => {
    const categories = new Set<string>();
    touchedQids.forEach(qid => {
      const category = qidToCategoryMap[qid];
      if (category) categories.add(category);
    });
    return categories;
  }, [touchedQids, qidToCategoryMap]);

  const errorCountByCategory = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const err of inlineValidationErrors) {
      const sections = (err.qids || [])
        .map(qid => qidToCategoryMap[qid])
        .filter(Boolean);
      for (const sec of new Set(sections)) {
        counts[sec] = (counts[sec] || 0) + 1;
      }
    }
    return counts;
  }, [inlineValidationErrors, qidToCategoryMap]);

  const navigateToError = useCallback(
    (err: OasisValidationError) => {
      const qid = (err.qids || [])[0];
      if (!qid) return;
      const category = qidToCategoryMap[qid];
      if (category && category !== currentCategory) {
        setCurrentCategory(category);
      }
      setHighlightedQid(qid);
      setTimeout(() => {
        const el = document.getElementById(`oasis-q-${qid}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      setTimeout(() => setHighlightedQid(null), 3000);
    },
    [qidToCategoryMap, currentCategory, setCurrentCategory]
  );

  const saveFormData = async (data: OASISFormData) => {
    try {
      setSaveStatus('saving');
      const dataToSave = schema ? getOASISFormData(data, schema) : data;
      await onSave(dataToSave);
      setLastSaved(new Date());
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Auto-save failed:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  // Debounced save function (save after 2 seconds of inactivity, or immediately if there are signatures)
  const debouncedSave = useDebounce(
    (data: OASISFormData) => {
      void saveFormData(data);
    },
    hasActiveSignatures ? 0 : 2000
  );

  const runInlineValidation = useCallback(
    async (data: OASISFormData) => {
      if (!showInlineValidation || !visitId || !schema) return;

      setInlineValidationLoading(true);
      setInlineValidationErrorMessage(null);

      try {
        const response = await validateOasisVisitsVisitIdValidateOasisPost({
          path: { visit_id: visitId },
          body: {
            answers: getOASISFormData(data, schema, {
              includeVisibleEmpty: true,
            }) as Record<string, unknown>,
          },
          client,
        });

        if (response.error) {
          const detail = (response.error as { detail?: string })?.detail;
          setInlineValidationErrorMessage(
            detail || 'Validation failed. Please try again.'
          );
          setInlineValidationErrors([]);
        } else {
          setInlineValidationErrors(response.data?.errors || []);
        }
      } catch (err) {
        console.error('Inline validation failed:', err);
        setInlineValidationErrorMessage('Validation failed. Please try again.');
        setInlineValidationErrors([]);
      } finally {
        setInlineValidationLoading(false);
        setHasValidatedOnce(true);
      }
    },
    [schema, showInlineValidation, visitId]
  );

  const debouncedInlineValidation = useDebounce((data: OASISFormData) => {
    void runInlineValidation(data);
  }, 900);

  const scrubberExpandedBodyRef = useRef<HTMLDivElement>(null);
  const scrubberStableBodyHeightRef = useRef(0);

  useLayoutEffect(() => {
    if (!showInlineValidation || !isOASIS) return;
    if (!isScrubberExpanded) {
      scrubberStableBodyHeightRef.current = 0;
      return;
    }
    if (inlineValidationLoading) return;
    const el = scrubberExpandedBodyRef.current;
    if (!el) return;
    scrubberStableBodyHeightRef.current = el.offsetHeight;
  }, [
    showInlineValidation,
    isOASIS,
    isScrubberExpanded,
    inlineValidationLoading,
    inlineValidationErrors,
    inlineValidationErrorMessage,
    hasValidatedOnce,
    scrubberTab,
    currentCategory,
  ]);

  useEffect(() => {
    setCurrentCategory(null);
  }, [visitType, setCurrentCategory]);

  useEffect(() => {
    const fetchSchema = async () => {
      setIsLoading(true);
      setError(null);

      try {
        let schemaData: OASISSchema;

        if (useVersionedFormSchemas) {
          const response = await getVisitSchemaRogerdocV2VisitsVisitIdSchemaGet(
            {
              path: { visit_id: visitId },
              client,
            }
          );

          if (!response?.data) {
            throw new Error(
              'Could not load the required form schema. Please contact support.'
            );
          }

          const { version, schema_data } = response.data as {
            version: FormSchemaVersion;
            schema_data: OASISSchema;
          };

          schemaData = schema_data;
          setSchemaVersion(version);

          if (version?.name) {
            try {
              const bespokeRes =
                await listEnabledSectionsBespokeTemplatesEnabledGet({
                  query: { schema_version_name: version.name },
                  client,
                });
              const entries =
                (bespokeRes?.data as EnabledBespokeEntry[] | undefined) ?? [];
              const sectionMap = new Map(
                entries.map(e => [
                  e.section_name,
                  {
                    section_name: e.section_name,
                    layout_json:
                      (e.layout_json as
                        | import('./bespoke/layout/types').LayoutNode[]
                        | null) ?? null,
                  },
                ])
              );
              setBespokeDbConfig(
                () => new Map([[version.name as string, sectionMap]])
              );
            } catch (bespokeErr) {
              console.warn('Failed to load bespoke config:', bespokeErr);
            }
          }
        } else {
          const response = await getQuestionSchemaRogerdocQuestionSchemaGet({
            query: { visit_type: visitType },
            client,
          });

          if (!response?.data) {
            throw new Error('Empty schema response');
          }

          schemaData = response.data as OASISSchema;
        }

        setSchema(schemaData);

        const categoryList = Object.keys(schemaData);
        if (isOASIS) {
          categoryList.push(ACTIVE_DIAGNOSIS_CATEGORY);
        }
        setCategories(categoryList);
        if (categoryList.length > 0) {
          setCurrentCategory(categoryList[0]);
        }
      } catch (err) {
        console.error('Failed to fetch question schema:', err);
        setError('Failed to load form schema. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSchema();
  }, [
    visitId,
    visitType,
    useVersionedFormSchemas,
    setSchema,
    setIsLoading,
    setError,
    setCurrentCategory,
    setSchemaVersion,
    setCategories,
    setBespokeDbConfig,
    isOASIS,
  ]);

  useEffect(() => {
    if (!schema) return;

    if (hydratedVisitIdRef.current !== visitId) {
      hydratedVisitIdRef.current = visitId;
      hasRunInitialValidation.current = false;

      const dataWithDefaults = initializePseudoQuestionDefaults(
        initialData || {},
        schema
      );
      setFormData(dataWithDefaults);

      if (showInlineValidation) {
        hasRunInitialValidation.current = true;
        void runInlineValidation(dataWithDefaults);
      }
      return;
    }

    if (showInlineValidation && !hasRunInitialValidation.current) {
      hasRunInitialValidation.current = true;
      const raw = useOASISFormStore.getState().formData;
      const dataWithDefaults = initializePseudoQuestionDefaults(raw, schema);
      void runInlineValidation(dataWithDefaults);
    }
    /* `initialData` is omitted on purpose: autosave updates `visit.visit_data` while the user may have newer edits; re-hydrating would flash stale values. */
  }, [visitId, schema, setFormData, showInlineValidation, runInlineValidation]);

  const handleCategoryChange = (category: string) => {
    setCurrentCategory(category);
  };

  const handleFormDataChange = (updates: Partial<OASISFormData>) => {
    const newFormData: OASISFormData = { ...formData };

    Object.entries(updates).forEach(([key, value]) => {
      if (value === undefined) {
        delete newFormData[key];
      } else {
        newFormData[key] = value;
      }
    });

    setTouchedQids(prev => {
      const next = new Set(prev);
      Object.keys(updates).forEach(key => {
        next.add(key);
      });
      return next;
    });

    setFormData(newFormData);
    debouncedSave(newFormData);
    if (showInlineValidation) {
      debouncedInlineValidation(newFormData);
    }
  };

  const renderInlineValidationPanel = () => {
    if (!showInlineValidation || !isOASIS) return null;

    const totalFatalCount = inlineValidationErrors.filter(
      err => err.severity === 'fatal'
    ).length;
    const totalWarningCount = inlineValidationErrors.filter(
      err => err.severity === 'warning'
    ).length;

    // Filter errors to current section; errors with no matching section go to "Misc"
    const sectionErrors = inlineValidationErrors.filter(err => {
      const sections = (err.qids || [])
        .map(qid => qidToCategoryMap[qid])
        .filter(Boolean);
      if (sections.length === 0) return currentCategory === null;
      return sections.includes(currentCategory || '');
    });
    const miscErrors = inlineValidationErrors.filter(err => {
      const sections = (err.qids || [])
        .map(qid => qidToCategoryMap[qid])
        .filter(Boolean);
      return sections.length === 0;
    });

    const visibleIssues =
      scrubberTab === 'section'
        ? sectionErrors
        : scrubberTab === 'misc'
          ? miscErrors
          : inlineValidationErrors;

    const scrubberNoIssuesMessage =
      scrubberTab === 'all'
        ? 'No issues'
        : scrubberTab === 'misc'
          ? 'No issues in Misc'
          : 'No issues in section';

    const highlightValidationText = (text: string) => {
      const matches = [...text.matchAll(VALIDATION_HIGHLIGHT_PATTERN)];
      if (matches.length === 0) return text;

      const nodes: React.ReactNode[] = [];
      let cursor = 0;
      matches.forEach((match, idx) => {
        const start = match.index ?? 0;
        const value = match[0];
        if (start > cursor) {
          nodes.push(text.slice(cursor, start));
        }
        nodes.push(
          <span
            key={`hl-${idx}-${start}`}
            className='font-medium text-foreground'
          >
            {value}
          </span>
        );
        cursor = start + value.length;
      });
      if (cursor < text.length) {
        nodes.push(text.slice(cursor));
      }
      return nodes;
    };

    return (
      <div className='mb-3 rounded-md border bg-muted/20 p-3'>
        <button
          type='button'
          onClick={() => setIsScrubberExpanded(prev => !prev)}
          className='w-full flex items-center justify-between gap-3 text-left'
          aria-expanded={isScrubberExpanded}
          aria-label='Toggle Live Scrubber'
        >
          <div className='flex items-center gap-2 text-sm min-w-0'>
            <AlertTriangle className='h-4 w-4 text-amber-600' />
            <span className='font-medium'>Live Scrubber</span>
            {inlineValidationLoading ? (
              <span className='text-xs text-muted-foreground'>Checking...</span>
            ) : inlineValidationErrorMessage ? (
              <span className='text-xs text-destructive'>
                Validation unavailable
              </span>
            ) : (
              <span className='text-xs text-muted-foreground'>
                Checks run automatically
              </span>
            )}
          </div>
          <div className='flex items-center gap-2'>
            {hasValidatedOnce && inlineValidationErrors.length > 0 && (
              <div className='flex gap-0.5' onClick={e => e.stopPropagation()}>
                {(['section', 'misc', 'all'] as const).map(tab => {
                  const count =
                    tab === 'section'
                      ? sectionErrors.length
                      : tab === 'misc'
                        ? miscErrors.length
                        : inlineValidationErrors.length;
                  return (
                    <button
                      key={tab}
                      type='button'
                      onClick={() => {
                        setScrubberTab(tab);
                        if (!isScrubberExpanded) setIsScrubberExpanded(true);
                      }}
                      className={`px-1.5 py-0.5 rounded text-[10px] font-medium transition-colors ${
                        scrubberTab === tab
                          ? 'bg-foreground/10 text-foreground'
                          : 'text-muted-foreground hover:text-foreground/70'
                      }`}
                    >
                      {tab === 'section'
                        ? formatCategoryLabel(currentCategory || '')
                        : tab === 'misc'
                          ? 'Misc'
                          : 'All'}
                      {count > 0 && (
                        <span className='ml-0.5 opacity-70'>({count})</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
            {(totalFatalCount > 0 || totalWarningCount > 0) && (
              <div className='flex items-center gap-2 text-xs'>
                {totalFatalCount > 0 && (
                  <span className='rounded bg-destructive/10 px-2 py-0.5 text-destructive'>
                    {totalFatalCount} fatal
                  </span>
                )}
                {totalWarningCount > 0 && (
                  <span className='rounded bg-amber-100 px-2 py-0.5 text-amber-700'>
                    {totalWarningCount} warning
                  </span>
                )}
              </div>
            )}
            {isScrubberExpanded ? (
              <ChevronUp className='h-4 w-4 text-muted-foreground' />
            ) : (
              <ChevronDown className='h-4 w-4 text-muted-foreground' />
            )}
          </div>
        </button>
        {isScrubberExpanded && (
          <div
            ref={scrubberExpandedBodyRef}
            className='mt-2 max-h-56 overflow-y-auto'
            style={
              inlineValidationLoading && scrubberStableBodyHeightRef.current > 0
                ? { minHeight: scrubberStableBodyHeightRef.current }
                : undefined
            }
          >
            {inlineValidationLoading ? (
              <div className='flex items-center gap-2 text-xs text-muted-foreground'>
                <Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />
                Running live checks...
              </div>
            ) : inlineValidationErrorMessage ? (
              <p className='text-xs text-destructive'>
                {inlineValidationErrorMessage}
              </p>
            ) : inlineValidationErrors.length > 0 ? (
              visibleIssues.length > 0 ? (
                <div className='space-y-2'>
                  {visibleIssues.map((err, idx) => {
                    const issueKey = `${err.edit_id}-${idx}`;
                    const formatted = formatValidationDescription(
                      err.description || ''
                    );
                    const {
                      type: ruleType,
                      condition,
                      expectation,
                    } = splitValidationRule(formatted);
                    const mappedSections = (err.qids || [])
                      .map(qid => qidToCategoryMap[qid])
                      .filter(Boolean)
                      .map(section => formatCategoryLabel(section as string));
                    const relatedSections =
                      mappedSections.length > 0
                        ? Array.from(new Set(mappedSections))
                        : ['Miscellaneous'];
                    const questionTexts = Array.from(
                      new Set(
                        (err.qids || [])
                          .map(qid => qidToQuestionText[qid])
                          .filter(Boolean)
                      )
                    );
                    const affectedFields =
                      err.oasis_fields?.length > 0
                        ? err.oasis_fields
                        : err.qids;
                    const visibleAffectedFields = affectedFields.slice(0, 5);
                    const extraAffectedCount = Math.max(
                      0,
                      affectedFields.length - 5
                    );
                    const isFatal = err.severity === 'fatal';
                    const detailInfo = parseValidationDetail(err.detail);
                    const valueItems = (err.values || []).filter(Boolean);
                    const firstValue =
                      valueItems[0]?.toLowerCase().trim() || '';
                    const observedValue =
                      detailInfo?.type === 'assignment'
                        ? (detailInfo.value || '').toLowerCase().trim()
                        : '';
                    const shouldShowObserved =
                      detailInfo?.type === 'assignment' &&
                      (!firstValue || observedValue !== firstValue);
                    const detailText =
                      detailInfo?.type === 'text'
                        ? formatValidationDescription(detailInfo.raw)
                        : '';
                    const detailPreview = detailText.slice(0, 220);
                    const allowedCodesPreview =
                      detailInfo?.type === 'notIn'
                        ? detailInfo.allowedCodes.slice(0, 8).join(', ')
                        : '';

                    return (
                      <div
                        key={issueKey}
                        className='rounded-md border bg-background/80 p-2.5 text-xs text-muted-foreground cursor-pointer hover:border-foreground/30 transition-colors'
                        onClick={() => navigateToError(err)}
                      >
                        <div className='flex flex-wrap items-start gap-1.5 text-[11px]'>
                          <span className='inline-flex items-center gap-1 text-muted-foreground'>
                            <svg
                              viewBox='0 0 6 6'
                              className='w-1.5 h-1.5 shrink-0 overflow-visible'
                            >
                              <circle
                                cx='3'
                                cy='3'
                                r='3'
                                className={
                                  isFatal
                                    ? 'fill-destructive'
                                    : 'fill-amber-500'
                                }
                              />
                            </svg>
                            {isFatal ? 'Fatal' : 'Warning'}
                          </span>
                          <span className='h-3.5 w-px bg-border' />
                          {relatedSections.slice(0, 2).map(section => (
                            <span
                              key={`${issueKey}-${section}`}
                              className='rounded bg-muted px-1.5 py-0.5 text-foreground/80'
                            >
                              {section}
                            </span>
                          ))}
                          {affectedFields.length > 0 && (
                            <>
                              <span className='h-3.5 w-px bg-border' />
                              <span className='rounded bg-muted/70 px-1.5 py-0.5'>
                                <span className='text-foreground/70'>
                                  Affected
                                </span>
                              </span>
                              <div className='flex flex-wrap gap-1'>
                                {visibleAffectedFields.map(field => (
                                  <span
                                    key={`${issueKey}-${field}`}
                                    className='rounded bg-muted px-1.5 py-0.5 font-mono text-foreground/80'
                                  >
                                    {field}
                                  </span>
                                ))}
                                {extraAffectedCount > 0 && (
                                  <span className='rounded bg-muted px-1.5 py-0.5 text-foreground/70'>
                                    +{extraAffectedCount} more
                                  </span>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                        {questionTexts.length > 0 && (
                          <p className='mt-1.5 text-[11px] text-foreground/70 italic'>
                            {questionTexts[0]}
                          </p>
                        )}
                        {condition ? (
                          <div className='mt-2 space-y-1'>
                            <div className='rounded bg-muted/40 px-2 py-1'>
                              <span className='mr-1 text-foreground/80 font-medium'>
                                {condition.includes('"') && expectation
                                  ? ruleType === 'ifThen'
                                    ? 'If:'
                                    : 'Item:'
                                  : 'If:'}
                              </span>
                              {highlightValidationText(condition)}
                            </div>
                            <div className='rounded bg-muted/20 px-2 py-1'>
                              <span className='mr-1 text-foreground/80 font-medium'>
                                {ruleType === 'ifThen' ? 'Then:' : 'Rule:'}
                              </span>
                              {highlightValidationText(expectation)}
                            </div>
                          </div>
                        ) : (
                          <div className='mt-2'>
                            {highlightValidationText(formatted)}
                          </div>
                        )}
                        {(valueItems.length > 0 || detailInfo) && (
                          <div className='mt-2 space-y-1 text-[11px] text-muted-foreground'>
                            {valueItems.length > 0 && (
                              <div>
                                <span className='text-foreground/70'>
                                  Current value:
                                </span>{' '}
                                <span className='font-mono text-foreground/80'>
                                  {valueItems.join(', ')}
                                </span>
                              </div>
                            )}
                            {detailInfo?.type === 'notIn' &&
                              allowedCodesPreview && (
                                <div>
                                  <span className='text-foreground/70'>
                                    Allowed codes:
                                  </span>{' '}
                                  <span className='font-mono text-foreground/80'>
                                    {allowedCodesPreview}
                                  </span>
                                  {detailInfo.allowedCodes.length > 8 && (
                                    <span className='text-muted-foreground'>
                                      {' '}
                                      +{detailInfo.allowedCodes.length - 8}
                                    </span>
                                  )}
                                </div>
                              )}
                            {shouldShowObserved && (
                              <div>
                                <span className='text-foreground/70'>
                                  Observed:
                                </span>{' '}
                                <span className='font-mono text-foreground/80'>
                                  {detailInfo?.type === 'assignment'
                                    ? detailInfo.value || 'N/A'
                                    : 'N/A'}
                                </span>
                              </div>
                            )}
                            {detailInfo?.type === 'text' && (
                              <div>
                                <span className='text-foreground/70'>
                                  Detail:
                                </span>{' '}
                                <span>
                                  {detailPreview}
                                  {detailText.length > 220 && '...'}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className='text-xs text-muted-foreground'>
                  {scrubberNoIssuesMessage}
                </div>
              )
            ) : hasValidatedOnce ? (
              <div className='text-xs text-muted-foreground'>
                {scrubberNoIssuesMessage}
              </div>
            ) : (
              <div className='text-xs text-muted-foreground'>
                Running checks for loaded data.
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const currentQuestions = useMemo(
    () => (currentCategory && schema ? schema[currentCategory] : []) || [],
    [currentCategory, schema]
  );

  const schemaQuestionOptions: SchemaQuestionOption[] = useMemo(() => {
    if (!schema) return [];
    const options: SchemaQuestionOption[] = [];
    const seen = new Set<string>();

    const collect = (questions: Question[], category: string) => {
      for (const q of questions) {
        if (!seen.has(q.qid)) {
          seen.add(q.qid);
          options.push({
            qid: q.qid,
            label: q.question || q.qid,
            category,
          });
        }
        if (q.subitems) {
          for (const children of Object.values(q.subitems)) {
            collect(children, category);
          }
        }
      }
    };

    for (const [category, questions] of Object.entries(schema)) {
      collect(questions, category);
    }
    return options;
  }, [schema]);

  const gridItems = useMemo(
    () => flattenQuestions(currentQuestions, spanOverrides),
    [currentQuestions, spanOverrides]
  );

  const handleSpanChange = useCallback((qid: string, span: GridSpan) => {
    setSpanOverrides(prev => ({ ...prev, [qid]: span }));
  }, []);

  /** Handle drag-and-drop reorder of grid items */
  const handleDrop = useCallback(
    (fromIdx: number, toIdx: number) => {
      if (!schema || !currentCategory || fromIdx === toIdx) return;

      const items = [...gridItems];
      const [moved] = items.splice(fromIdx, 1);
      items.splice(toIdx, 0, moved);

      const newQuestions = reconstructQuestions(items, currentQuestions);
      const newSchema = { ...schema, [currentCategory]: newQuestions };
      setSchema(newSchema);
    },
    [schema, currentCategory, gridItems, currentQuestions, setSchema]
  );

  /** Handle drag-and-drop reorder for non-OASIS sections */
  const handleNonOasisDrop = useCallback(
    (category: string, fromIdx: number, toIdx: number) => {
      if (!schema || fromIdx === toIdx) return;

      const categoryQuestions = schema[category] || [];
      const items = [...flattenQuestions(categoryQuestions, spanOverrides)];
      const [moved] = items.splice(fromIdx, 1);
      items.splice(toIdx, 0, moved);

      const newQuestions = reconstructQuestions(items, categoryQuestions);
      const newSchema = { ...schema, [category]: newQuestions };
      setSchema(newSchema);
    },
    [schema, setSchema, spanOverrides]
  );

  const handleSubgroupDrop = useCallback(
    (parentQid: string, fromIdx: number, toIdx: number) => {
      if (!schema || !currentCategory || fromIdx === toIdx) return;

      const categoryQuestions = schema[currentCategory] || [];
      const updatedQuestions = reorderNestedSubitemsInQuestions(
        categoryQuestions,
        parentQid,
        fromIdx,
        toIdx
      );
      if (updatedQuestions === categoryQuestions) return;

      setSchema({ ...schema, [currentCategory]: updatedQuestions });
    },
    [schema, currentCategory, setSchema]
  );

  const handleNonOasisSubgroupDrop = useCallback(
    (category: string, parentQid: string, fromIdx: number, toIdx: number) => {
      if (!schema || fromIdx === toIdx) return;

      const categoryQuestions = schema[category] || [];
      const updatedQuestions = reorderNestedSubitemsInQuestions(
        categoryQuestions,
        parentQid,
        fromIdx,
        toIdx
      );
      if (updatedQuestions === categoryQuestions) return;

      setSchema({ ...schema, [category]: updatedQuestions });
    },
    [schema, setSchema]
  );

  /** Handle drag-and-drop reorder of section cards (non-OASIS) */
  const handleSectionDrop = useCallback(
    (fromIdx: number, toIdx: number) => {
      if (!schema || fromIdx === toIdx) return;

      const newCategories = [...categories];
      const [moved] = newCategories.splice(fromIdx, 1);
      newCategories.splice(toIdx, 0, moved);
      setCategories(newCategories);

      // Rebuild schema with new key order
      const newSchema: OASISSchema = {};
      for (const cat of newCategories) {
        newSchema[cat] = schema[cat];
      }
      setSchema(newSchema);
    },
    [schema, categories, setSchema]
  );

  const handleCopySchema = useCallback(() => {
    if (!schema) return;

    /**
     * Emit two compact directive maps:
     *
     * question_order_by_section:
     *   section → ordered array of top-level qids, reflecting the current
     *   arrangement. Paste into question_schema_builder to change order.
     *
     * subitem_order_by_parent_qid:
     *   parent qid → ordered array of direct child qids for nested subitem groups.
     *   Paste into question_schema_builder tooling to change nested order.
     *
     * roger_emr_display_width_by_qid:
     *   qid → width (1-12) for every question whose current effective width
     *   differs from the default (3). Paste values into question_db.json entries.
     *
     * These two structures are the only degrees of freedom controlled here;
     * all other schema data lives in question_db / question_schema_builder.
     */

    const questionOrderBySection: Record<string, string[]> = {};
    const subitemOrderByParentQid: Record<string, string[]> = {};
    const widthByQid: Record<string, number> = {};

    const collectWidths = (questions: Question[]) => {
      for (const q of questions) {
        const span = resolveQuestionSpan(q, spanOverrides);
        // Include when there is an explicit user override (even if it equals the
        // default 3) so that "reset to default" intent is expressible downstream.
        const hasExplicitOverride = spanOverrides[q.qid] !== undefined;
        if (hasExplicitOverride || span !== DEFAULT_GRID_SPAN) {
          widthByQid[q.qid] = span;
        }
        if (q.subitems) {
          for (const subs of Object.values(q.subitems)) {
            collectWidths(subs);
          }
        }
      }
    };

    for (const [category, questions] of Object.entries(schema)) {
      // Top-level qid ordering (what question_schema_builder tracks)
      questionOrderBySection[category] = questions.map(q => q.qid);
      Object.assign(
        subitemOrderByParentQid,
        collectSubitemOrderByParentQid(questions)
      );
      collectWidths(questions);
    }

    const artifact = {
      question_order_by_section: questionOrderBySection,
      subitem_order_by_parent_qid: subitemOrderByParentQid,
      roger_emr_display_width_by_qid: widthByQid,
    };

    navigator.clipboard.writeText(JSON.stringify(artifact, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [schema, spanOverrides]);

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
      </div>
    );
  }

  if (error) {
    return <p className='text-destructive text-sm py-4'>{error}</p>;
  }

  if (!schema || categories.length === 0) {
    return (
      <p className='text-muted-foreground text-sm py-4'>
        No form schema available for this visit type.
      </p>
    );
  }

  /** Span selector shown on each card in arrange mode */
  const renderSpanSelector = (qid: string, currentSpan: GridSpan) => (
    <div className='absolute top-1 right-1 z-10 grid grid-cols-4 gap-px bg-muted rounded-md p-0.5'>
      {QUESTION_SPAN_OPTIONS.map(s => (
        <button
          key={s}
          type='button'
          onClick={e => {
            e.stopPropagation();
            handleSpanChange(qid, s);
          }}
          className={`w-5 h-5 text-[9px] font-medium rounded transition-colors ${
            currentSpan === s
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:bg-background hover:text-foreground'
          }`}
        >
          {s}
        </button>
      ))}
    </div>
  );

  /** Section span selector shown in non-OASIS arrange mode */
  const renderSectionSpanSelector = (
    category: string,
    currentSpan: SectionGridSpan
  ) => (
    <div className='grid grid-cols-4 gap-px bg-muted rounded-md p-0.5'>
      {SECTION_SPAN_OPTIONS.map(s => (
        <button
          key={`${category}-${s}`}
          type='button'
          onClick={e => {
            e.stopPropagation();
            setSectionSpanOverrides(prev => ({ ...prev, [category]: s }));
          }}
          className={`w-5 h-5 text-[9px] font-medium rounded transition-colors ${
            currentSpan === s
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:bg-background hover:text-foreground'
          }`}
          aria-label={`Set section width ${s}`}
          title={`Width ${s}`}
        >
          {s}
        </button>
      ))}
    </div>
  );

  const renderSubgroupChildren = (
    children: GridItem[],
    parentSpan: GridSpan,
    parentQid: string,
    onSubgroupDrop: (parentQid: string, fromIdx: number, toIdx: number) => void
  ) => (
    <div className='grid grid-cols-12 gap-2'>
      {children.map((child, childIndex) => {
        const spanClass = getSubgroupChildSpanClass(child.span, parentSpan);
        const isDragging =
          subgroupDragIndex?.parentQid === parentQid &&
          subgroupDragIndex.index === childIndex;
        const isDragOver =
          subgroupDragOverIndex?.parentQid === parentQid &&
          subgroupDragOverIndex.index === childIndex;

        return (
          <div
            key={child.question.qid}
            className={`${spanClass} relative min-w-0 ${isDragging ? 'opacity-40' : ''} ${isDragOver ? 'ring-2 ring-primary rounded-lg' : ''}`}
            draggable
            onDragStart={e => {
              e.stopPropagation();
              setSubgroupDragIndex({ parentQid, index: childIndex });
              e.dataTransfer.effectAllowed = 'move';
              e.dataTransfer.setData(
                'application/subgroup-question',
                JSON.stringify({ parentQid, index: childIndex })
              );
            }}
            onDragOver={e => {
              if (
                !e.dataTransfer.types.includes('application/subgroup-question')
              )
                return;
              const payload = e.dataTransfer.getData(
                'application/subgroup-question'
              );
              if (!payload) return;

              const dragData = JSON.parse(payload) as {
                parentQid: string;
                index: number;
              };
              if (dragData.parentQid !== parentQid) return;

              e.preventDefault();
              e.stopPropagation();
              e.dataTransfer.dropEffect = 'move';
              setSubgroupDragOverIndex({ parentQid, index: childIndex });
            }}
            onDragLeave={() => {
              if (
                subgroupDragOverIndex?.parentQid === parentQid &&
                subgroupDragOverIndex.index === childIndex
              ) {
                setSubgroupDragOverIndex(null);
              }
            }}
            onDrop={e => {
              if (
                !e.dataTransfer.types.includes('application/subgroup-question')
              )
                return;
              const payload = e.dataTransfer.getData(
                'application/subgroup-question'
              );
              if (!payload) return;

              const dragData = JSON.parse(payload) as {
                parentQid: string;
                index: number;
              };
              if (dragData.parentQid !== parentQid) return;

              e.preventDefault();
              e.stopPropagation();
              setSubgroupDragIndex(null);
              setSubgroupDragOverIndex(null);
              onSubgroupDrop(parentQid, dragData.index, childIndex);
            }}
            onDragEnd={() => {
              setSubgroupDragIndex(null);
              setSubgroupDragOverIndex(null);
            }}
          >
            {renderSpanSelector(child.question.qid, child.span)}
            {!child.subgroupChildren && (
              <div className='absolute left-0 top-0 bottom-0 flex items-center z-10 -ml-1 cursor-grab active:cursor-grabbing'>
                <GripVertical className='h-4 w-4 text-muted-foreground/60' />
              </div>
            )}
            <div className={child.subgroupChildren ? 'pt-14' : 'pl-4'}>
              {child.subgroupChildren ? (
                <>
                  <div className='flex items-center gap-1.5 mb-2'>
                    <GripVertical className='h-4 w-4 text-muted-foreground/60 cursor-grab active:cursor-grabbing flex-shrink-0' />
                    <p className='text-[11px] font-semibold text-muted-foreground uppercase tracking-wide'>
                      {child.question.question ?? child.question.description}
                    </p>
                  </div>
                  {renderSubgroupChildren(
                    child.subgroupChildren,
                    child.span,
                    child.question.qid,
                    onSubgroupDrop
                  )}
                </>
              ) : (
                <OASISQuestionRenderer
                  question={child.question}
                  value={formData[child.question.qid]}
                  onChange={() => {}}
                  formData={formData}
                  spanOverrides={spanOverrides}
                  containerSpan={child.span}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  /** Renders a question grid item with optional drag handle and span controls */
  const renderGridItem = (
    item: GridItem,
    index: number,
    onDrop: (from: number, to: number) => void,
    onSubgroupDrop: (parentQid: string, fromIdx: number, toIdx: number) => void
  ) => {
    const spanClass = SPAN_CLASSES[item.span];

    if (arrangeMode) {
      const isDragging = dragIndex === index;
      const isDragOver = dragOverIndex === index;

      return (
        <div
          key={item.question.qid}
          className={`${spanClass} min-w-0 relative ${isDragging ? 'opacity-40' : ''} ${isDragOver ? 'ring-2 ring-primary rounded-lg' : ''}`}
          draggable
          onDragStart={e => {
            e.stopPropagation();
            setDragIndex(index);
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('application/question', String(index));
          }}
          onDragOver={e => {
            if (!e.dataTransfer.types.includes('application/question')) return;
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = 'move';
            setDragOverIndex(index);
          }}
          onDragLeave={() => {
            if (dragOverIndex === index) setDragOverIndex(null);
          }}
          onDrop={e => {
            if (!e.dataTransfer.types.includes('application/question')) return;
            e.preventDefault();
            e.stopPropagation();
            const from = parseInt(
              e.dataTransfer.getData('application/question'),
              10
            );
            setDragIndex(null);
            setDragOverIndex(null);
            onDrop(from, index);
          }}
          onDragEnd={() => {
            setDragIndex(null);
            setDragOverIndex(null);
          }}
        >
          {renderSpanSelector(item.question.qid, item.span)}
          {!item.subgroupChildren && (
            <div className='absolute left-0 top-0 bottom-0 flex items-center z-10 -ml-1 cursor-grab active:cursor-grabbing'>
              <GripVertical className='h-4 w-4 text-muted-foreground/60' />
            </div>
          )}
          <div className={item.subgroupChildren ? 'pt-14' : 'pl-4'}>
            {item.subgroupChildren ? (
              // Sub-group: show parent label then a mini-grid of individually configurable children
              <>
                <div className='flex items-center gap-1.5 mb-2'>
                  <GripVertical className='h-4 w-4 text-muted-foreground/60 cursor-grab active:cursor-grabbing flex-shrink-0' />
                  <p className='text-[11px] font-semibold text-muted-foreground uppercase tracking-wide'>
                    {item.question.question ?? item.question.description}
                  </p>
                </div>
                {renderSubgroupChildren(
                  item.subgroupChildren,
                  item.span,
                  item.question.qid,
                  onSubgroupDrop
                )}
              </>
            ) : (
              <OASISQuestionRenderer
                question={item.question}
                value={formData[item.question.qid]}
                onChange={() => {}}
                formData={formData}
                spanOverrides={spanOverrides}
                containerSpan={item.span}
              />
            )}
          </div>
        </div>
      );
    }

    return (
      <div key={item.question.qid} className={`${spanClass} min-w-0`}>
        <OASISQuestionRenderer
          question={item.question}
          value={formData[item.question.qid]}
          onChange={value => {
            const updates: Partial<OASISFormData> = {
              [item.question.qid]: value,
            };

            if (item.pseudoParent) {
              updates[item.pseudoParent.qid] = 'Yes';
            }

            const clearUpdates = getSubitemClearUpdates(
              item.question,
              value,
              formData
            );
            Object.assign(updates, clearUpdates);

            handleFormDataChange(updates);
          }}
          formData={formData}
          spanOverrides={spanOverrides}
          containerSpan={item.span}
          onFormDataChange={updates => {
            if (item.pseudoParent) {
              updates[item.pseudoParent.qid] = 'Yes';
            }
            handleFormDataChange(updates);
          }}
          highlightedQid={highlightedQid}
        />
      </div>
    );
  };

  /** Arrange mode toolbar (only for users with emr:manage) */
  const arrangeToolbar = canManageEmr ? (
    <div className='flex items-center gap-2'>
      <Button
        variant={arrangeMode ? 'default' : 'outline'}
        size='sm'
        onClick={() => setArrangeMode(prev => !prev)}
        className='text-xs'
      >
        <ArrowUpDown className='h-3 w-3 mr-1.5' />
        {arrangeMode ? 'Done' : 'Arrange'}
      </Button>
      {arrangeMode && (
        <Button
          variant='outline'
          size='sm'
          onClick={handleCopySchema}
          className='text-xs'
        >
          {copied ? (
            <Check className='h-3 w-3 mr-1.5 text-green-600' />
          ) : (
            <Copy className='h-3 w-3 mr-1.5' />
          )}
          {copied ? 'Copied' : 'Copy Directives'}
        </Button>
      )}
    </div>
  ) : null;

  /** Save status indicator */
  const saveStatusIndicator = (
    <div className='flex items-center gap-1'>
      {saveStatus === 'saving' && (
        <Loader2 className='h-3 w-3 animate-spin text-muted-foreground' />
      )}
      {saveStatus === 'saved' && <Check className='h-3 w-3 text-green-600' />}
      {saveStatus === 'error' && (
        <span className='text-[10px] text-destructive'>Error</span>
      )}
    </div>
  );

  const renderOasisSectionContent = () => {
    const BespokeComponent = resolveBespokeSection(
      bespokeDbConfig,
      schemaVersion?.name,
      currentCategory || ''
    );

    if (BespokeComponent && !arrangeMode && schemaVersion) {
      return (
        <>
          <div className='mb-4 pb-2 border-b border-border flex items-center justify-between'>
            <h3 className='text-base font-bold text-foreground'>
              {formatCategoryLabel(currentCategory || '')}
            </h3>
            {arrangeToolbar}
          </div>
          {renderInlineValidationPanel()}
          <BespokeComponent
            answers={formData}
            onAnswerChange={(qid, value) =>
              handleFormDataChange({ [qid]: value })
            }
            onFormDataChange={handleFormDataChange}
            schema={currentQuestions}
            schemaQuestionOptions={schemaQuestionOptions}
            sectionName={currentCategory || ''}
          />
        </>
      );
    }

    return (
      <>
        <div className='mb-4 pb-2 border-b border-border flex items-center justify-between'>
          <h3 className='text-base font-bold text-foreground'>
            {formatCategoryLabel(currentCategory || '')}
          </h3>
          {arrangeToolbar}
        </div>
        {renderInlineValidationPanel()}

        {arrangeMode && (
          <p className='text-xs text-muted-foreground mb-3'>
            Drag questions to reorder. Use width presets (
            <strong>1,2,3,4,6,8,9,12</strong>) on each card to set a 12-column
            layout width. Click <strong>Copy Directives</strong> to export a
            compact JSON with question ordering (for{' '}
            <code>question_schema_builder</code>) and non-default widths (for{' '}
            <code>question_db</code>).
          </p>
        )}

        <div className='grid grid-cols-12 gap-3'>
          {gridItems.map((item, idx) =>
            renderGridItem(item, idx, handleDrop, handleSubgroupDrop)
          )}
        </div>
      </>
    );
  };

  // ─── Non-OASIS layout: full-width section cards stacked vertically ───
  if (!isOASIS) {
    return (
      <div>
        {renderInlineValidationPanel()}
        <div className='flex items-center justify-between mb-4'>
          <div className='flex items-center gap-3'>
            <span className='text-[10px] font-semibold text-muted-foreground uppercase tracking-wider'>
              {categories.length} sections
            </span>
            {saveStatusIndicator}
          </div>
          {arrangeToolbar}
        </div>

        <div className='grid grid-cols-4 gap-4 items-start'>
          {categories.map((category, sectionIdx) => {
            const categoryQuestions = schema[category] || [];
            const items = flattenQuestions(categoryQuestions, spanOverrides);
            const currentSectionSpan =
              sectionSpanOverrides[category] ?? DEFAULT_SECTION_SPAN;
            const isSectionDragging = sectionDragIndex === sectionIdx;
            const isSectionDragOver = sectionDragOverIndex === sectionIdx;

            return (
              <Card
                key={category}
                className={`${SECTION_SPAN_CLASSES[currentSectionSpan]} ${
                  arrangeMode
                    ? `${isSectionDragging ? 'opacity-40' : ''} ${isSectionDragOver ? 'ring-2 ring-primary' : ''}`
                    : ''
                }`}
                draggable={arrangeMode}
                onDragStart={
                  arrangeMode
                    ? e => {
                        e.stopPropagation();
                        setSectionDragIndex(sectionIdx);
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData(
                          'application/section',
                          String(sectionIdx)
                        );
                      }
                    : undefined
                }
                onDragOver={
                  arrangeMode
                    ? e => {
                        // Only handle section-level drops
                        if (
                          e.dataTransfer.types.includes('application/section')
                        ) {
                          e.preventDefault();
                          e.dataTransfer.dropEffect = 'move';
                          setSectionDragOverIndex(sectionIdx);
                        }
                      }
                    : undefined
                }
                onDragLeave={
                  arrangeMode
                    ? () => {
                        if (sectionDragOverIndex === sectionIdx)
                          setSectionDragOverIndex(null);
                      }
                    : undefined
                }
                onDrop={
                  arrangeMode
                    ? e => {
                        if (
                          e.dataTransfer.types.includes('application/section')
                        ) {
                          e.preventDefault();
                          e.stopPropagation();
                          const from = parseInt(
                            e.dataTransfer.getData('application/section'),
                            10
                          );
                          setSectionDragIndex(null);
                          setSectionDragOverIndex(null);
                          handleSectionDrop(from, sectionIdx);
                        }
                      }
                    : undefined
                }
                onDragEnd={
                  arrangeMode
                    ? () => {
                        setSectionDragIndex(null);
                        setSectionDragOverIndex(null);
                      }
                    : undefined
                }
              >
                <CardContent className='pt-4 pb-5'>
                  <div className='flex items-center gap-2 mb-4 pb-2 border-b border-border relative'>
                    {arrangeMode && (
                      <GripVertical className='h-4 w-4 text-muted-foreground/60 cursor-grab active:cursor-grabbing flex-shrink-0' />
                    )}
                    <h4 className='text-sm font-bold text-foreground uppercase tracking-wide flex-1'>
                      {formatCategoryLabel(category)}
                    </h4>
                    {arrangeMode &&
                      renderSectionSpanSelector(category, currentSectionSpan)}
                  </div>
                  {(() => {
                    const BespokeComponent = resolveBespokeSection(
                      bespokeDbConfig,
                      schemaVersion?.name,
                      category
                    );
                    if (BespokeComponent && !arrangeMode && schemaVersion) {
                      return (
                        <BespokeComponent
                          answers={formData}
                          onAnswerChange={(qid, value) =>
                            handleFormDataChange({ [qid]: value })
                          }
                          onFormDataChange={handleFormDataChange}
                          schema={categoryQuestions}
                          schemaQuestionOptions={schemaQuestionOptions}
                          sectionName={category}
                        />
                      );
                    }
                    return (
                      <div className='grid grid-cols-12 gap-3'>
                        {items.map((item, idx) =>
                          renderGridItem(
                            item,
                            idx,
                            (from, to) =>
                              handleNonOasisDrop(category, from, to),
                            (parentQid, from, to) =>
                              handleNonOasisSubgroupDrop(
                                category,
                                parentQid,
                                from,
                                to
                              )
                          )
                        )}
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  // ─── OASIS layout: sidebar navigation + single category view ───
  return (
    <div className='flex'>
      {/* Sidebar navigation */}
      <div className='w-48 flex-shrink-0 pr-4 border-r border-border'>
        <div className='sticky top-0'>
          <div className='flex items-center justify-between mb-2 px-2.5'>
            <span className='text-[10px] font-semibold text-muted-foreground uppercase tracking-wider'>
              Sections
            </span>
            {saveStatusIndicator}
          </div>
          <OASISNavigation
            categories={categories}
            currentCategory={currentCategory}
            onCategoryChange={handleCategoryChange}
            schema={schema}
            formData={formData}
            touchedCategories={touchedCategories}
            errorCountByCategory={errorCountByCategory}
          />
        </div>
      </div>

      {/* Form content */}
      <div className='flex-1 min-w-0 pl-5'>
        {currentCategory === ACTIVE_DIAGNOSIS_CATEGORY ? (
          <>
            <div className='mb-4 pb-2 border-b border-border'>
              <h3 className='text-base font-bold text-foreground'>
                Active Diagnosis
              </h3>
            </div>
            <VisitActiveDiagnosis visitId={visitId} />
          </>
        ) : (
          renderOasisSectionContent()
        )}
      </div>
    </div>
  );
};
